import { isUuid, uuid } from './uuid'

/**
 * The browser session an event was recorded in (VH-2669). A session is a tab,
 * not a funnel attempt — one tab holds many; `attempt_id` identifies those.
 *
 * This is a *browser* session and has nothing to do with `IvSessionSource` on
 * the server, which describes the provenance of a verification unit of work.
 * The two share the word and nothing else.
 *
 * Held in `sessionStorage`, not `localStorage`, and that is a correctness
 * choice rather than a storage one: sessionStorage is per-tab. A user working
 * two patients in two tabs produces two independent funnels, and a shared id
 * would interleave their steps into one sequence that never happened.
 */

const SESSION_ID_KEY = 'vh_behavior_session_id'
const LAST_ACTIVITY_KEY = 'vh_behavior_session_last_activity'

/**
 * Thirty minutes, the usual analytics definition of a visit. Distinct from the
 * app's own 15-minute auth idle timeout, which logs the user out and rotates
 * this id through `resetBehaviorSession()` anyway — so in practice this only
 * fires for a tab left open on a public page.
 */
const IDLE_TIMEOUT_MS = 30 * 60 * 1000

/**
 * Every accessor is wrapped: sessionStorage throws on access in some privacy
 * modes and when site data is blocked. Instrumentation must never be the
 * reason a page breaks, so a storage failure degrades to a per-call id rather
 * than propagating.
 */
function read(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    // Ignored: see above.
  }
}

function remove(key: string): void {
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    // Ignored: see above.
  }
}

/**
 * The current session id, starting or rotating one as needed.
 *
 * Reading also marks activity, which is what keeps an actively-used tab on one
 * session id: the idle clock measures the gap between events, not the age of
 * the session.
 */
export function currentBehaviorSessionId(): string {
  const now = Date.now()
  const lastActivity = Number(read(LAST_ACTIVITY_KEY) ?? 0)
  const existing = read(SESSION_ID_KEY)

  const expired = lastActivity > 0 && now - lastActivity > IDLE_TIMEOUT_MS

  // A stored id that is not a uuid is replaced, not reused. The old fallback
  // wrote those, and sessionStorage outlives a reload, so a tab that picked
  // one up would otherwise have every event rejected until its session
  // rotated.
  if (existing === null || !isUuid(existing) || expired) {
    const id = uuid()

    write(SESSION_ID_KEY, id)
    write(LAST_ACTIVITY_KEY, String(now))

    return id
  }

  write(LAST_ACTIVITY_KEY, String(now))

  return existing
}

/**
 * Start a new session. Called on login and on logout.
 *
 * Both ends matter. Without a rotation at logout, the next person to sign in
 * at a shared front-desk workstation inherits the previous user's session id
 * and their two funnels merge into one.
 */
export function resetBehaviorSession(): void {
  remove(SESSION_ID_KEY)
  remove(LAST_ACTIVITY_KEY)
}
